import { COPY } from '@/shared/constants/copies';
import { DOCS_LINKS } from '@/shared/constants/docs';

import { OFFER_LETTER_LIMITS, PLAN_CREDITS } from './plan-limits';

export const COMPARE_PLAN_KEYS = ['free', 'starter', 'business', 'pro'] as const;

export type ComparePlanKey = (typeof COMPARE_PLAN_KEYS)[number];

export type CompareCell =
	| { type: 'included' }
	| { type: 'excluded' }
	| { type: 'text'; value: string };

export type CompareRow = {
	feature: string;
	/** Docs page the feature name links to. Omit for rows with nothing to explain. */
	href?: string;
	/** Info-icon text. A row carries one whenever the matching card feature line does. */
	tooltip?: string;
	cells: Record<ComparePlanKey, CompareCell>;
};

const included: CompareCell = { type: 'included' };
const excluded: CompareCell = { type: 'excluded' };
const unlimited: CompareCell = { type: 'text', value: COPY.pricing.compareUnlimited };

const allIncluded = {
	free: included,
	starter: included,
	business: included,
	pro: included,
} satisfies Record<ComparePlanKey, CompareCell>;

const allUnlimited = {
	free: unlimited,
	starter: unlimited,
	business: unlimited,
	pro: unlimited,
} satisfies Record<ComparePlanKey, CompareCell>;

const fromStarter = {
	free: excluded,
	starter: included,
	business: included,
	pro: included,
} satisfies Record<ComparePlanKey, CompareCell>;

const fromBusiness = {
	free: excluded,
	starter: excluded,
	business: included,
	pro: included,
} satisfies Record<ComparePlanKey, CompareCell>;

export const COMPARE_PLAN_LABELS: Record<ComparePlanKey, string> = {
	free: COPY.pricing.planNameFree,
	starter: COPY.pricing.tierStarter,
	business: COPY.pricing.tierBusiness,
	pro: COPY.pricing.tierPro,
};

export const PLAN_COMPARE_ROWS: CompareRow[] = [
	{ feature: 'Jobs', cells: allUnlimited },
	{ feature: 'Candidates', cells: allUnlimited },
	{ feature: 'Team members', href: DOCS_LINKS.membersAndRoles, cells: allUnlimited },
	{ feature: 'AI agent', href: DOCS_LINKS.aiAgent, cells: allIncluded },
	{ feature: 'Slack agent', href: DOCS_LINKS.slack, cells: allIncluded },
	{ feature: 'MCP support', href: DOCS_LINKS.mcp, cells: allIncluded },
	{ feature: 'Careers page', href: DOCS_LINKS.careersPage, cells: allIncluded },
	{ feature: 'Email candidates', href: DOCS_LINKS.emailCandidates, cells: allIncluded },
	{ feature: 'Chrome extension', href: DOCS_LINKS.chromeExtension, cells: allIncluded },
	{
		feature: 'Stage changes',
		tooltip: COPY.pricing.compareTooltipStageChanges,
		cells: {
			free: { type: 'text', value: '100/month' },
			starter: unlimited,
			business: unlimited,
			pro: unlimited,
		},
	},
	{ feature: 'Duplicate detection', cells: fromStarter },
	{
		feature: 'Analytics',
		cells: {
			free: excluded,
			starter: { type: 'text', value: 'Basic' },
			business: { type: 'text', value: 'Advanced' },
			pro: { type: 'text', value: 'Advanced' },
		},
	},
	{ feature: 'Admin settings', cells: fromStarter },
	{ feature: 'Job board integration', cells: fromBusiness },
	{
		feature: 'Interview scheduling',
		href: DOCS_LINKS.interviewScheduling,
		cells: {
			free: { type: 'text', value: '3/month' },
			starter: { type: 'text', value: '10/month' },
			business: unlimited,
			pro: unlimited,
		},
	},
	{ feature: 'Interview feedback', href: DOCS_LINKS.interviewFeedback, cells: allIncluded },
	{
		feature: 'Offer letters',
		href: DOCS_LINKS.offerLetters,
		cells: {
			free: { type: 'text', value: `${OFFER_LETTER_LIMITS.free}/month` },
			starter: { type: 'text', value: `${OFFER_LETTER_LIMITS.starter}/month` },
			business: { type: 'text', value: `${OFFER_LETTER_LIMITS.business}/month` },
			pro: unlimited,
		},
	},
	{
		feature: 'Assignments',
		href: DOCS_LINKS.assignments,
		cells: {
			free: { type: 'text', value: '100/month' },
			starter: { type: 'text', value: '1000/month' },
			business: unlimited,
			pro: unlimited,
		},
	},
	{
		feature: 'Storage',
		cells: {
			free: { type: 'text', value: '30 GB' },
			starter: { type: 'text', value: '100 GB' },
			business: { type: 'text', value: '500 GB' },
			pro: unlimited,
		},
	},
	{
		feature: 'Credits',
		href: DOCS_LINKS.usageAndCredits,
		tooltip: COPY.pricing.compareTooltipCredits,
		cells: {
			free: { type: 'text', value: `$${PLAN_CREDITS.free}/month` },
			starter: { type: 'text', value: `$${PLAN_CREDITS.starter}/month` },
			business: { type: 'text', value: `$${PLAN_CREDITS.business}/month` },
			pro: { type: 'text', value: `$${PLAN_CREDITS.pro}/month` },
		},
	},
];
